/**
 * BrowseFilterStrip — founder direction 9 Feb 2026 ·
 * "in the tabs you have made for Michelin etc the filters come below that"
 *
 * Per-tab filter chip strip with LIVE COUNTS, GRACEFUL-EMPTY, and
 * URL-encoded state.
 *
 * For each tab we render the right facet set:
 *   - michelin     · Where · Stars · Verification
 *   - bars         · Where · Authority (W50B)
 *   - nightclubs   · Where · Authority (DJ Mag)
 *   - fine_dine    · Where
 *   - all          · Where
 *
 * Live counts: every chip shows the count of venues that would
 * survive if clicked. Counts come from `/api/table/browse-counts`.
 *
 * Graceful-empty: chips with count=0 are HIDDEN entirely (not
 * disabled). Whole filter groups with no populated values disappear.
 *
 * URL-encoded: every selection writes to the address bar via the
 * onChange callback · the parent owns the URL update.
 */
import React, { useEffect, useState, useCallback } from "react";
import { BACKEND_URL } from "@/lib/backend-url";


const TAB_CONFIG = {
    michelin: ["where", "stars", "cuisine", "verification"],
    bars: ["where", "cuisine", "authority_w50b"],
    nightclubs: ["where", "authority_djmag"],
    fine_dine: ["where", "cuisine"],
    all: ["where", "cuisine"],
    destinations: [], // city picker is the primary filter on this tab
};

// Founder direction · 29 Jun 2026 · filter consistency.
// Same 14 popular cuisines that the FilterBar (results view) ships
// with, so the language stays identical across views. The list is
// deliberately not exhaustive · power users still get the free-typed
// cuisine input on results view.
const BROWSE_CUISINES = [
    "Italian", "Japanese", "Indian", "Chinese", "Thai",
    "Mexican", "French", "Spanish", "Korean", "Vietnamese",
    "Mediterranean", "American", "Lebanese", "Peruvian",
];


export function BrowseFilterStrip({ tab, filters, onChange, onClearAll, embedded = false, filteredTotal = null }) {
    const [counts, setCounts] = useState(null);
    const [loading, setLoading] = useState(false);

    const fetchCounts = useCallback(async () => {
        if (!tab) return;
        setLoading(true);
        try {
            const params = new URLSearchParams({ tab });
            if (filters?.city) params.set("city", filters.city);
            const r = await fetch(
                `${BACKEND_URL}/api/table/browse-counts?${params}`,
            );
            if (r.ok) {
                setCounts(await r.json());
            }
        } catch {
            // soft-fail · render nothing rather than break the grid
        } finally {
            setLoading(false);
        }
    }, [tab, filters?.city]);

    useEffect(() => {
        fetchCounts();
    }, [fetchCounts]);

    if (!tab || tab === "destinations") return null;
    const groups = TAB_CONFIG[tab] || [];
    if (groups.length === 0) return null;
    if (!counts && !loading) return null;

    // Helpful for the graceful-empty check
    const hasAny = (group) => {
        if (group === "where") {
            return counts?.where && Object.keys(counts.where).length > 0;
        }
        if (group === "stars") {
            // BC-024 · BIB lives inside the same "stars" panel; show
            // the group if either stars OR bib have counts.
            const hasStars = counts?.stars && Object.keys(counts.stars).length > 0;
            const hasBib = (counts?.bib_gourmand || 0) > 0;
            return hasStars || hasBib;
        }
        if (group === "verification") {
            return counts?.verification?.confirmed > 0;
        }
        if (group === "authority_w50b") {
            return counts?.authority?.world_50_best_bars_2025 > 0;
        }
        if (group === "authority_djmag") {
            return counts?.authority?.dj_mag_top_100_2026 > 0;
        }
        if (group === "cuisine") {
            // Cuisine pills are always available — they don't need
            // count data because they're a refinement chip set, not
            // a discovery facet. Browse counts API doesn't return
            // cuisine breakdown · we ship the same 14 popular
            // cuisines the FilterBar (results view) ships.
            return true;
        }
        return false;
    };
    const visibleGroups = groups.filter(hasAny);
    if (visibleGroups.length === 0) return null;

    return (
        <div
            data-testid="browse-filter-strip"
            data-tab={tab}
            style={embedded ? {
                paddingInline: 14,
                paddingTop: 10,
                paddingBottom: 12,
            } : {
                maxWidth: 1152,
                marginInline: "auto",
                paddingInline: 24,
                paddingTop: 14,
                paddingBottom: 18,
                borderBottom: "1px solid var(--t-line2, rgba(0,0,0,0.06))",
            }}
        >
            <div
                style={{
                    fontSize: 10,
                    fontFamily: "ui-monospace, monospace",
                    letterSpacing: "0.18em",
                    color: "var(--t-ink3, rgba(0,0,0,0.55))",
                    textTransform: "uppercase",
                    marginBottom: 10,
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                }}
            >
                {/* BC-017 · founder catch 10 Feb 2026 · "filters not
                   working" was actually the COUNT pretending the
                   filters weren't honoured. The chip click DID re-fire
                   the API correctly (16 returned for stars=1,3) ·
                   the strip just kept showing the unfiltered city
                   total (19) from /browse-counts. The parent now
                   threads its filtered `total` down as
                   `filteredTotal` so this line tells the truth. */}
                <span>refine · {filteredTotal ?? counts?.total ?? 0} venues</span>
                {(filters?.city || filters?.stars?.length || filters?.authority || filters?.verification) ? (
                    <button
                        type="button"
                        data-testid="browse-filter-clear-all"
                        onClick={() => {
                            // BC-007 (founder catch 10 Feb 2026) ·
                            // "Clear all" in a tab view must wipe the
                            // tab + city + every filter and return the
                            // member to the clean /table home · NEVER
                            // sideways into a wider filtered set of
                            // the same trap. `onClearAll` (when wired)
                            // is the authoritative exit; the
                            // `onChange({})` fallback keeps prior
                            // standalone callers backward-compatible.
                            if (typeof onClearAll === "function") {
                                onClearAll();
                            } else {
                                onChange({});
                            }
                        }}
                        style={{
                            fontSize: 10, fontFamily: "ui-monospace, monospace",
                            letterSpacing: "0.18em", padding: "3px 9px",
                            borderRadius: 999, border: "1px solid var(--t-line2)",
                            background: "transparent", cursor: "pointer",
                            color: "var(--t-ink2)", textTransform: "uppercase",
                        }}
                    >
                        clear all
                    </button>
                ) : null}
            </div>
            <div
                style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 16,
                    rowGap: 12,
                    alignItems: "flex-start",
                }}
            >
                {visibleGroups.includes("where")
                    ? <WhereGroup counts={counts.where}
                                  active={filters?.city}
                                  onSelect={(city) =>
                                      onChange({ ...filters, city: city || undefined })}
                      />
                    : null}
                {visibleGroups.includes("stars")
                    ? <StarsGroup counts={counts.stars}
                                  bibCount={counts.bib_gourmand || 0}
                                  active={filters?.stars}
                                  bibActive={filters?.distinction === "bib_gourmand"}
                                  onSelect={(stars) =>
                                      onChange({ ...filters, stars })}
                                  onBibSelect={(active) =>
                                      onChange({
                                          ...filters,
                                          distinction: active ? "bib_gourmand" : undefined,
                                      })}
                      />
                    : null}
                {visibleGroups.includes("verification")
                    ? <VerificationGroup confirmedN={counts.verification.confirmed}
                                        active={filters?.verification}
                                        onSelect={(v) =>
                                            onChange({ ...filters, verification: v || undefined })}
                      />
                    : null}
                {visibleGroups.includes("authority_w50b")
                    ? <AuthorityGroup
                          label="World's 50 Best Bars 2025"
                          value="world_50_best_bars_2025"
                          count={counts.authority.world_50_best_bars_2025}
                          active={filters?.authority === "world_50_best_bars_2025"}
                          onSelect={(v) => onChange({ ...filters, authority: v || undefined })}
                      />
                    : null}
                {visibleGroups.includes("authority_djmag")
                    ? <AuthorityGroup
                          label="DJ Mag Top 100 Clubs 2026"
                          value="dj_mag_top_100_2026"
                          count={counts.authority.dj_mag_top_100_2026}
                          active={filters?.authority === "dj_mag_top_100_2026"}
                          onSelect={(v) => onChange({ ...filters, authority: v || undefined })}
                      />
                    : null}
                {visibleGroups.includes("cuisine")
                    ? <CuisineGroup
                          active={filters?.cuisine || ""}
                          onChange={(next) =>
                              onChange({ ...filters, cuisine: next || undefined })}
                      />
                    : null}
            </div>
        </div>
    );
}


function FilterGroupHeader({ label }) {
    return (
        <div
            className="t-mono uppercase"
            style={{
                fontSize: 9,
                letterSpacing: "0.18em",
                color: "var(--t-ink3, rgba(0,0,0,0.55))",
                marginBottom: 6,
            }}
        >
            {label}
        </div>
    );
}


function Chip({ children, active, onClick, count, testid, tone }) {
    // BC-024 · 10 Feb 2026 PM · optional `tone="slate"` for the
    // BIB chip · so it reads visually distinct from the brass
    // star chips even when active. Brass-star chips keep the
    // ink active background; slate chips use a slate active.
    const isSlate = tone === "slate";
    const activeBg = isSlate ? "var(--t-slate, #6B6960)" : "var(--t-ink, #211C15)";
    const activeBorder = isSlate ? "var(--t-slate2, #6B6960)" : "var(--t-line2, rgba(0,0,0,0.12))";
    return (
        <button
            type="button"
            data-testid={testid}
            data-active={active ? "true" : "false"}
            data-tone={tone || undefined}
            onClick={onClick}
            style={{
                fontSize: 12,
                padding: "5px 11px",
                borderRadius: 999,
                border: `1px solid ${active ? activeBorder : "var(--t-line2, rgba(0,0,0,0.12))"}`,
                background: active ? activeBg : "transparent",
                color: active ? "var(--t-paper, #F4EFE6)" : "var(--t-ink2, rgba(0,0,0,0.7))",
                cursor: "pointer",
                fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
                letterSpacing: "0.01em",
                whiteSpace: "nowrap",
                transition: "background 120ms ease, color 120ms ease",
            }}
        >
            {children}
            {count != null ? (
                <span style={{
                    marginLeft: 6, opacity: 0.6, fontSize: 11,
                }}>· {count}</span>
            ) : null}
        </button>
    );
}


function WhereGroup({ counts, active, onSelect }) {
    const entries = Object.entries(counts).slice(0, 10);
    return (
        <div data-testid="filter-group-where">
            <FilterGroupHeader label="where" />
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {entries.map(([city, n]) => (
                    <Chip key={city}
                          testid={`filter-where-${city.toLowerCase().replace(/\W+/g, "-")}`}
                          active={active === city}
                          count={n}
                          onClick={() => onSelect(active === city ? null : city)}>
                        {city}
                    </Chip>
                ))}
            </div>
        </div>
    );
}


function StarsGroup({ counts, bibCount, active, bibActive, onSelect, onBibSelect }) {
    // BC-024 · 10 Feb 2026 PM (founder direction · "Bib can come
    // under Michelin"). The MICHELIN distinction filter now ships
    // 4 chips: ★ · ★★ · ★★★ · BIB.  The BIB chip is rendered in
    // SILVER (slate hue) so the member can tell it apart from the
    // brass star chips at a glance · still a MICHELIN selection,
    // honestly distinct from starred fine-dining.
    const stars = Object.entries(counts || {})
        .map(([k, n]) => [parseInt(k, 10), n])
        .filter(([k]) => [1, 2, 3].includes(k))
        .sort((a, b) => a[0] - b[0]);
    const activeSet = new Set(active || []);
    if (stars.length === 0 && !bibCount) return null;
    return (
        <div data-testid="filter-group-stars">
            <FilterGroupHeader label="michelin distinction" />
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {stars.map(([k, n]) => {
                    const isActive = activeSet.has(k);
                    return (
                        <Chip key={k}
                              testid={`filter-stars-${k}`}
                              active={isActive}
                              count={n}
                              onClick={() => {
                                  const next = new Set(activeSet);
                                  if (isActive) next.delete(k); else next.add(k);
                                  const arr = [...next].sort();
                                  onSelect(arr.length ? arr : undefined);
                              }}>
                            {"★".repeat(k)}
                        </Chip>
                    );
                })}
                {bibCount > 0 ? (
                    <Chip testid="filter-bib"
                          active={!!bibActive}
                          count={bibCount}
                          tone="slate"
                          onClick={() => onBibSelect?.(!bibActive)}>
                        BIB
                    </Chip>
                ) : null}
            </div>
        </div>
    );
}


function VerificationGroup({ confirmedN, active, onSelect }) {
    return (
        <div data-testid="filter-group-verification">
            <FilterGroupHeader label="verification" />
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                <Chip testid="filter-verification-confirmed"
                      active={active === "confirmed"}
                      count={confirmedN}
                      onClick={() => onSelect(active === "confirmed" ? null : "confirmed")}>
                    Confirmed by Concierge®
                </Chip>
            </div>
        </div>
    );
}


function AuthorityGroup({ label, value, count, active, onSelect }) {
    return (
        <div data-testid={`filter-group-authority-${value}`}>
            <FilterGroupHeader label="authority" />
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                <Chip testid={`filter-authority-${value}`}
                      active={active}
                      count={count}
                      onClick={() => onSelect(active ? null : value)}>
                    {label}
                </Chip>
            </div>
        </div>
    );
}


function CuisineGroup({ active, onChange }) {
    // Multi-select cuisine pill row · filter consistency (29 Jun 2026).
    // Mirrors the FilterBar (results view) so the member sees the
    // same shortlist of 14 popular cuisines on both surfaces.
    const activeSet = new Set(
        (active || "").split(",").map(s => s.trim()).filter(Boolean)
            .map(s => s.toLowerCase())
    );
    const toggle = (cuisine) => {
        const key = cuisine.toLowerCase();
        const next = new Set(activeSet);
        if (next.has(key)) next.delete(key); else next.add(key);
        const arr = [...next];
        // Preserve original casing for the canonical list when re-emitting.
        const pretty = BROWSE_CUISINES.filter(c => next.has(c.toLowerCase()));
        onChange?.(pretty.length ? pretty.join(",") : null);
        return arr;
    };
    return (
        <div data-testid="filter-group-cuisine">
            <FilterGroupHeader label="cuisine" />
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {BROWSE_CUISINES.map((c) => {
                    const isActive = activeSet.has(c.toLowerCase());
                    return (
                        <Chip
                            key={c}
                            testid={`filter-cuisine-${c.toLowerCase()}`}
                            active={isActive}
                            onClick={() => toggle(c)}
                        >
                            {c}
                        </Chip>
                    );
                })}
            </div>
        </div>
    );
}
